/**
 * Writes for the seller-parity admin endpoints. Each one needs a `reason`
 * (logged, and shown to the seller in their notification), refreshes every
 * view of the store and toasts — see `useStoreAction`.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useClientStore } from '@/client/stores/client';
import { getApiErrorMessage } from '@/client/utils/errors';
import { useStoreAction } from './stores.mutation';
import { isStoreScopedKey } from './stores.query';
import type {
  StoreInventoryAdjustment,
  StorePayoutConfigUpdate,
  StorePriceUpdate,
  StoreVoucherCreate,
} from '@/client/types/stores/parity';

const BASE = '/api/admin/stores';

type R = { storeid: number; reason: string };

// ── Orders ──────────────────────────────────────────────────────────────────

export const useUpdateStoreOrderStatus = () =>
  useStoreAction<R & { orderid: number; status: string; tracking_number?: string; cancel_reason?: string; payment_collected?: boolean; collected_by?: 'seller' | 'rider' }>(
    (p) => `${BASE}/${p.storeid}/orders/${p.orderid}/status`, 'PUT',
    (p) => (p.status === 'cancelled' ? 'Order cancelled' : `Order marked ${p.status}`),
  );

export const useResolveStoreRefundRequest = () =>
  useStoreAction<R & { rrid: number; action: 'approve' | 'reject'; seller_notes?: string }>(
    (p) => `${BASE}/${p.storeid}/refund-requests/${p.rrid}`, 'PUT',
    (p) => (p.action === 'approve' ? 'Refund request approved' : 'Refund request declined'),
  );

export const useResolveStoreReturnRequest = () =>
  useStoreAction<R & { rtid: number; action: 'approve' | 'reject'; seller_notes?: string }>(
    (p) => `${BASE}/${p.storeid}/return-requests/${p.rtid}`, 'PUT',
    (p) => (p.action === 'approve' ? 'Return approved' : 'Return declined'),
  );

export const useMarkStoreReturnReceived = () =>
  useStoreAction<R & { rtid: number; item_condition: 'resellable' | 'damaged' }>(
    (p) => `${BASE}/${p.storeid}/return-requests/${p.rtid}/received`, 'PUT', 'Return marked received',
  );

// ── Inventory, listings, pricing ────────────────────────────────────────────

export const useAdjustStoreInventory = () =>
  useStoreAction<R & { eiid: number } & StoreInventoryAdjustment>(
    (p) => `${BASE}/${p.storeid}/inventory/${p.eiid}`, 'PUT', 'Inventory updated',
  );

export const useLinkStoreVariants = () =>
  useStoreAction<R & { eids: number[] }>((p) => `${BASE}/${p.storeid}/variants`, 'POST', 'Listings linked as variants');

export const useUnlinkStoreVariant = () =>
  useStoreAction<R & { eid: number }>(
    (p) => `${BASE}/${p.storeid}/variants/${p.eid}`, 'DELETE', 'Listing unlinked from its variants',
  );

/** A catalogue listing's essentials (condition, colour, storage, warranty, delivery, SKU, barcode). */
export const useUpdateStoreCatalogDetails = () =>
  useStoreAction<R & { eid: number } & Record<string, string | number>>(
    (p) => `${BASE}/${p.storeid}/listing/${p.eid}/details`, 'PUT', 'Listing details updated',
  );

export const useAttachStoreListing = () =>
  useStoreAction<R & { eid: number }>((p) => `${BASE}/${p.storeid}/entity`, 'POST', 'Listing added to store');

export const useDetachStoreListing = () =>
  useStoreAction<R & { eid: number }>(
    (p) => `${BASE}/${p.storeid}/entity/${p.eid}`, 'DELETE', 'Listing removed from store',
  );

export const useBulkUpdateStorePrices = () =>
  useStoreAction<R & { updates: StorePriceUpdate[] }>(
    (p) => `${BASE}/${p.storeid}/prices/bulk`, 'POST', 'Prices updated',
  );

// ── Money ───────────────────────────────────────────────────────────────────

export const useUpdateStorePayoutConfig = () =>
  useStoreAction<R & StorePayoutConfigUpdate>((p) => `${BASE}/${p.storeid}/payout/config`, 'PUT', 'Payout settings saved');

export const useCancelStoreRenewal = () =>
  useStoreAction<R>((p) => `${BASE}/${p.storeid}/plan/renewal/cancel`, 'POST', 'Renewal cancelled');

export const useResumeStoreRenewal = () =>
  useStoreAction<R>((p) => `${BASE}/${p.storeid}/plan/renewal/resume`, 'POST', 'Renewal resumed');

// ── Vouchers ────────────────────────────────────────────────────────────────

export const useCreateStoreVoucher = () =>
  useStoreAction<R & StoreVoucherCreate>((p) => `${BASE}/${p.storeid}/vouchers`, 'POST', 'Voucher created');

export const useRevokeStoreVoucher = () =>
  useStoreAction<R & { vid: number }>((p) => `${BASE}/${p.storeid}/vouchers/${p.vid}/revoke`, 'POST', 'Voucher revoked');

// ── Staff ───────────────────────────────────────────────────────────────────

/** Drop one per-code override; the member falls back to their role default. */
export const useRemoveStoreOverride = () =>
  useStoreAction<R & { uid: number; code: string }>(
    (p) => `${BASE}/${p.storeid}/permissions/users/${p.uid}/${encodeURIComponent(p.code)}`, 'DELETE',
    'Override removed',
  );

/** Replace a member's overrides with an org-role preset (1 owner … 4 viewer). */
export const useApplyStorePreset = () =>
  useStoreAction<R & { uid: number; preset_ourid: 1 | 2 | 3 | 4 }>(
    (p) => `${BASE}/${p.storeid}/permissions/users/${p.uid}/preset`, 'POST', 'Preset applied',
  );

// ── Profile attributes, media, venue, delivery ──────────────────────────────

export const useUpdateStoreAttributes = () =>
  useStoreAction<R & { attributes: Record<string, unknown> }>(
    (p) => `${BASE}/${p.storeid}/attributes`, 'PUT', 'Store details saved',
  );

/** Logo (`photo`) and banner routes, spelled out so the parity check can see them. */
const IMAGE_PATH = { photo: '/photo', banner: '/banner' } as const;

export const useClearStoreImage = () =>
  useStoreAction<R & { kind: 'photo' | 'banner' }>(
    (p) => `${BASE}/${p.storeid}${IMAGE_PATH[p.kind]}`, 'DELETE', (p) => (p.kind === 'photo' ? 'Logo removed' : 'Banner removed'),
  );

export const useSetStoreVenue = () =>
  useStoreAction<R & { eid: number; allow_claimed?: boolean }>((p) => `${BASE}/${p.storeid}/venue`, 'PUT', 'Venue linked');

export const useClearStoreVenue = () =>
  useStoreAction<R>((p) => `${BASE}/${p.storeid}/venue`, 'DELETE', 'Venue unlinked');

export const useUpdateStoreDelivery = () =>
  useStoreAction<R & { lids: number[] }>((p) => `${BASE}/${p.storeid}/delivery-locations`, 'PUT', 'Delivery coverage saved');

/**
 * Upload a logo (`photo`) or banner. Multipart (`files[]`), which the API proxy
 * forwards as FormData; the reason travels as a query param since it can't
 * sit in a JSON body.
 */
export const useUploadStoreImage = () => {
  const request = useClientStore((st) => st.request);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (p: R & { kind: 'photo' | 'banner'; file: File }) => {
      const form = new FormData();
      form.append('files[]', p.file, p.file.name);
      form.append('reason', p.reason);
      return request(`${BASE}/${p.storeid}${IMAGE_PATH[p.kind]}?reason=${encodeURIComponent(p.reason)}`, 'POST', form, { timeout: 60_000 });
    },
    onSuccess: (_d, p) => {
      qc.invalidateQueries({ predicate: (q) => isStoreScopedKey(q.queryKey, p.storeid) });
      toast.success(p.kind === 'photo' ? 'Logo updated' : 'Banner updated');
    },
    onError: (err) => toast.error(getApiErrorMessage(err, 'Upload failed')),
  });
};

// ── Branches (ff-api business rev 0510) ─────────────────────────────────────

export interface StoreBranchFields {
  name?: string;
  eid?: number | null;
  lid?: number | null;
  pickup_enabled?: boolean;
  active?: boolean;
  delivery_locations?: number[];
}

export const useCreateStoreBranch = () =>
  useStoreAction<R & StoreBranchFields & { allow_claimed?: boolean }>(
    (p) => `${BASE}/${p.storeid}/branches`, 'POST', 'Branch added',
  );

export const useUpdateStoreBranch = () =>
  useStoreAction<R & { branch_id: number } & StoreBranchFields>(
    (p) => `${BASE}/${p.storeid}/branches/${p.branch_id}`, 'PUT', 'Branch updated',
  );

export const useDeleteStoreBranch = () =>
  useStoreAction<R & { branch_id: number }>(
    (p) => `${BASE}/${p.storeid}/branches/${p.branch_id}`, 'DELETE', 'Branch removed',
  );

export const useSetStorePrimaryBranch = () =>
  useStoreAction<R & { branch_id: number }>(
    (p) => `${BASE}/${p.storeid}/branches/${p.branch_id}/primary`, 'POST', 'Main branch changed',
  );

export const useSetStoreBranchVenue = () =>
  useStoreAction<R & { branch_id: number; eid: number; allow_claimed?: boolean }>(
    (p) => `${BASE}/${p.storeid}/branches/${p.branch_id}/venue`, 'PUT', 'Branch linked to place',
  );

export const useClearStoreBranchVenue = () =>
  useStoreAction<R & { branch_id: number }>(
    (p) => `${BASE}/${p.storeid}/branches/${p.branch_id}/venue`, 'DELETE', 'Branch unlinked from place',
  );

export const useTransferStoreStock = () =>
  useStoreAction<R & { eid: number; quantity: number; from_branch_id: number; to_branch_id: number }>(
    (p) => `${BASE}/${p.storeid}/inventory/transfer`, 'POST', 'Stock moved',
  );

export const useMoveStoreOrderBranch = () =>
  useStoreAction<R & { orderid: number; branch_id: number }>(
    (p) => `${BASE}/${p.storeid}/orders/${p.orderid}/branch`, 'PUT', 'Order moved to branch',
  );
